import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../components/AdminLayout';
import adminApi from '../services/adminApi';

const EMPTY_FORM = {
  slug: '', title: '', category: 'Actualité', author: '',
  excerpt: '', content: '', image: '', is_published: true,
  published_at: new Date().toISOString().split('T')[0],
};

const AdminActualites = () => {
  const [news, setNews]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal]     = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm]       = useState(EMPTY_FORM);
  const [saving, setSaving]   = useState(false);
  const [alert, setAlert]     = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    adminApi.getNews()
      .then(({ data }) => setNews(data.data))
      .catch(() => setAlert({ type: 'error', msg: 'Impossible de charger les actualités.' }))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setEditing(null);
    setModal('form');
    setAlert(null);
  };

  const openEdit = (n) => {
    setForm({
      slug: n.slug, title: n.title, category: n.category ?? 'Actualité',
      author: n.author ?? '', excerpt: n.excerpt, content: n.content ?? '',
      image: n.image ?? '', is_published: n.is_published,
      published_at: n.published_at ?? new Date().toISOString().split('T')[0],
    });
    setEditing(n.id);
    setModal('form');
    setAlert(null);
    adminApi.getNewsItem(n.id).then(({ data }) => {
      setForm(prev => ({ ...prev, content: data.data.content ?? '' }));
    }).catch(() => {});
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setAlert(null);
    try {
      if (editing) {
        await adminApi.updateNews(editing, form);
        setAlert({ type: 'success', msg: 'Article mis à jour.' });
      } else {
        await adminApi.createNews(form);
        setAlert({ type: 'success', msg: 'Article créé.' });
      }
      setModal(null);
      load();
    } catch (err) {
      const msgs = err.response?.data?.errors;
      const firstMsg = msgs ? Object.values(msgs)[0]?.[0] : err.response?.data?.message;
      setAlert({ type: 'error', msg: firstMsg || 'Erreur lors de la sauvegarde.' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cet article ? Cette action est irréversible.')) return;
    try {
      await adminApi.deleteNews(id);
      setAlert({ type: 'success', msg: 'Article supprimé.' });
      load();
    } catch {
      setAlert({ type: 'error', msg: "Impossible de supprimer cet article." });
    }
  };

  const f = (field) => (e) =>
    setForm({ ...form, [field]: field === 'is_published' ? e.target.checked : e.target.value });

  return (
    <AdminLayout>
      <div className="admin-page-header">
        <h1>Actualités</h1>
        <p>Gérez les articles et actualités de la fondation</p>
      </div>

      {alert && !modal && (
        <div className={`admin-alert ${alert.type}`}>{alert.msg}</div>
      )}

      <div className="admin-card">
        <div className="admin-card-header">
          <h2>Articles ({news.length})</h2>
          <button className="admin-btn primary" onClick={openCreate}>+ Nouvel article</button>
        </div>

        {loading ? (
          <p style={{ color: '#aaa', textAlign: 'center', padding: 32 }}>Chargement…</p>
        ) : news.length === 0 ? (
          <div className="admin-empty">
            <div className="empty-icon">📰</div>
            <p>Aucun article. Créez le premier !</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Titre</th>
                  <th>Catégorie</th>
                  <th>Auteur</th>
                  <th>Publié le</th>
                  <th>Statut</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {news.map((n) => (
                  <tr key={n.id}>
                    <td style={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <strong>{n.title}</strong>
                    </td>
                    <td>{n.category}</td>
                    <td>{n.author || '—'}</td>
                    <td>{n.published_at || '—'}</td>
                    <td>
                      <span className={`admin-badge-active ${n.is_published ? 'yes' : 'no'}`}>
                        {n.is_published ? 'Publié' : 'Brouillon'}
                      </span>
                    </td>
                    <td>
                      <div className="table-actions">
                        <button className="admin-btn secondary" onClick={() => openEdit(n)}>Modifier</button>
                        <button className="admin-btn danger" onClick={() => handleDelete(n.id)}>Supprimer</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modal === 'form' && (
        <div className="admin-form-overlay" onClick={() => setModal(null)}>
          <div className="admin-form-modal" style={{ maxWidth: 720 }} onClick={(e) => e.stopPropagation()}>
            <h3>{editing ? "Modifier l'article" : 'Nouvel article'}</h3>

            {alert?.type === 'error' && (
              <div className="admin-alert error">{alert.msg}</div>
            )}

            <form onSubmit={handleSave}>
              <div className="form-row">
                <div className="form-group">
                  <label>Slug *</label>
                  <input value={form.slug} onChange={f('slug')} placeholder="ex: mon-article-2026" required />
                </div>
                <div className="form-group">
                  <label>Catégorie</label>
                  <select value={form.category} onChange={f('category')}>
                    {['Actualité','Programmes','Partenariats','Fondation','Événements'].map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Titre *</label>
                <input value={form.title} onChange={f('title')} required />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Auteur</label>
                  <input value={form.author} onChange={f('author')} placeholder="Équipe BE TALENTED" />
                </div>
                <div className="form-group">
                  <label>Date de publication</label>
                  <input type="date" value={form.published_at} onChange={f('published_at')} />
                </div>
              </div>

              <div className="form-group">
                <label>Image (chemin ou URL)</label>
                <input value={form.image} onChange={f('image')} placeholder="/assets/IMG/photo.jpg" />
              </div>

              <div className="form-group">
                <label>Extrait *</label>
                <textarea value={form.excerpt} onChange={f('excerpt')} required rows={3} placeholder="Résumé court de l'article…" />
              </div>

              <div className="form-group">
                <label>Contenu (HTML autorisé) *</label>
                <textarea value={form.content} onChange={f('content')} required rows={8} placeholder="<p>Contenu complet…</p>" />
              </div>

              <div className="form-group">
                <label>
                  <input type="checkbox" checked={form.is_published} onChange={f('is_published')} style={{ marginRight: 8 }} />
                  Publier l'article
                </label>
              </div>

              <div className="form-actions">
                <button type="button" className="admin-btn secondary" onClick={() => setModal(null)}>Annuler</button>
                <button type="submit" className="admin-btn primary" disabled={saving}>
                  {saving ? 'Enregistrement…' : 'Enregistrer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminActualites;
